import React, { useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import { EditUser, RemoveUser, ViewUser } from "../../../../services/authorization/Permissions";
import {
  ActionsSection,
  ButtonsSection,
  DataSection,
  SubSection,
} from "../../../../components/data-components/sections/DataSections";
import PageLayout from "../../../_layout/PageLayout";
import EditButton from "../../../../components/buttons/edit-button/EditButton";
import DisplayImage from "../../../../components/data-components/DisplayImage";
import DisplayDescription from "../../../../components/data-components/DisplayDescription";
import DisplayDateAdded from "../../../../components/data-components/DisplayDateAdded";
import DisplayAddedBy from "../../../../components/data-components/DisplayAddedBy";
import {
  DisplayEmail,
  DisplayEmploymentStatus,
  DisplayFirstName,
  DisplayLastName,
  DisplayPhone,
  DisplayUserName,
  DisplayUserRole,
} from "../../components/DataComponents";
import { UserMenuItemsModel } from "../../UserMenuItems";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { HeaderProfile } from "../../../../components/svg/svgImageList";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import StopCollaborationButton from "../../components/buttons/StopCollaborationButton";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import { UserInViewStorageModel } from "../../../../services/user/UserInViewStorage";
import { api } from "../../../../services/api/ApiService";
import { EmploymentStatus } from "../../../../services/user/EmploymentStatus";
import { Role } from "../../../../services/user/Role";
import { CurrentOrganizationStorageModel } from "../../../../services/organization/CurrentOrganizationStorage";
import { currentUserService } from "../../../../services/user/CurrentUserStorage";
import { UserRoles } from "../../../../generated/Users";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const userInViewId = urlParamsService.getUserId()!;

  const [userInViewStorage] = useState(() =>
    UserInViewStorageModel.create({ userInViewId: userInViewId }, { api })
  );

  const [currentOrganizationStorage] = useState(() =>
    CurrentOrganizationStorageModel.create(
      { autoLoad: currentUserService.currentUser.role.id === UserRoles.Admin },
      { api }
    )
  );

  const menuItems = useMemo(() => {
    const userMenuItems = UserMenuItemsModel.create({
      userId: userInViewId,
      isLoading: userInViewStorage.isLoading,
      organizationHasStripeCustomer: currentOrganizationStorage.organization.hasStripeCustomer,
    });

    return {
      staticItems: userMenuItems.getStaticItems(),
    };
  }, [
    currentOrganizationStorage.organization.hasStripeCustomer,
    userInViewId,
    userInViewStorage.isLoading,
  ]);

  const goToEditUser = () => {
    history.push(`/users/${userInViewId}/edit`);
  };

  const handleRemoveUser = async () => {
    try {
      await userInViewStorage.removeUser();
      history.push("/users");
      snackbarNotificationsService.showSuccess("ViewUser.remove.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.user.remove");
      handleErrorInSnackbar(applicationError);
    }
  };

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewUser]}
      titleKey={userInViewStorage.userInView.name ? "ViewUser.title" : "ViewUser.title-no-username"}
      titleContext={{ userName: userInViewStorage.userInView.name }}
      id="view-user-details"
      menuItems={menuItems}
      isLoading={userInViewStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <EditButton
            disabled={!permissionsService.userHasPermissionTo(EditUser)}
            onClick={goToEditUser}
          />
          <StopCollaborationButton
            disabled={!permissionsService.userHasPermissionTo(RemoveUser)}
            confirmationAction={handleRemoveUser}
            confirmationModalTitleKey="ViewUser.remove.confirmation.title"
            confirmationModalSubtitleKey="ViewUser.remove.confirmation"
            optionalWarningKey={
              userInViewStorage.userInView.lockedInspectionsCount > 0
                ? userInViewStorage.userInView.lockedInspectionsCount > 1
                  ? "ViewUser.remove.locked-inspections"
                  : "ViewUser.remove.locked-inspection"
                : undefined
            }
            optionalWarningContext={
              userInViewStorage.userInView.lockedInspectionsCount > 0
                ? { count: userInViewStorage.userInView.lockedInspectionsCount }
                : {}
            }
          />
        </ButtonsSection>
      </ActionsSection>
      <DataSection labelKey="ViewUser.details">
        <SubSection>
          {userInViewStorage.hasImageUploaded ? (
            <DisplayImage id="user-image" url={userInViewStorage.imageUrl} />
          ) : (
            <DisplayImage id="user-image" placeholderImage={HeaderProfile} />
          )}
        </SubSection>
        <SubSection>
          <DisplayEmail email={userInViewStorage.userInView.email} />
          <DisplayEmploymentStatus
            employmentStatus={new EmploymentStatus(userInViewStorage.userInView.employmentStatus)}
            organizationName={
              userInViewStorage.userInView.currentOrganization
                ? userInViewStorage.userInView.currentOrganization?.name
                : ""
            }
          />
          <DisplayUserRole role={new Role(userInViewStorage.userInView.role)} />
        </SubSection>

        <SubSection>
          <DisplayUserName
            userName={userInViewStorage.userInView.userName}
            supportTag={userInViewStorage.userInView.isSupportAccount}
          />
          <DisplayFirstName name={userInViewStorage.userInView.firstName} />
          <DisplayLastName name={userInViewStorage.userInView.lastName} />
          <DisplayPhone phone={userInViewStorage.userInView.phone} />
        </SubSection>

        <SubSection>
          <DisplayDateAdded date={userInViewStorage.userInView.invitedToOrganizationDate} />
          <DisplayAddedBy addedBy={userInViewStorage.userInView.invitedToOrganizationBy.name} />
        </SubSection>
      </DataSection>

      <DataSection labelKey="general.comments">
        <DisplayDescription
          description={userInViewStorage.userInView.comments}
          labelKey="general.comments"
        />
      </DataSection>
    </PageLayout>
  );
});
